import { useState } from 'react';
import { monoLabel } from '../UI/ink';
import { typeLabel } from './model';

const glyph = (a) => ({
  width: 14, height: 14,
  borderRadius: a?.kind === 'network' ? 2 : a?.kind === 'server' ? 3 : 7,
  border: '1.5px dashed var(--ink-muted)',
  transform: a?.kind === 'network' ? 'rotate(45deg) scale(0.85)' : undefined,
});

const acceptBtn = 'h-9 px-3.5 rounded-[9px] text-[13px] font-semibold disabled:opacity-50';
const quietBtn = 'h-9 px-3 rounded-[9px] border text-[13px] disabled:opacity-50';

// Under the map: discovered devices and private addresses agents talked to that
// no asset has, each one decision away from being on (or off) the map.
const ReviewTray = ({ discovered, unknown, byId, onSelect, onReview, onAddAddress }) => {
  const [busy, setBusy] = useState(null);
  const total = discovered.length + unknown.length;

  // The agent that found the most devices gets a one-click "accept all".
  const byFinder = {};
  discovered.forEach(a => { if (a.discoveredBy) (byFinder[a.discoveredBy] = byFinder[a.discoveredBy] || []).push(a); });
  const [finder, found] = Object.entries(byFinder).sort((x, y) => y[1].length - x[1].length)[0] || [];

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } finally { setBusy(null); }
  };

  return (
    <section aria-label="Waiting for review" className="rounded-[14px] border flex flex-col" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
        <span className="flex items-baseline gap-2.5">
          <h2 className={`m-0 font-medium ${monoLabel}`} style={{ color: 'var(--ink-muted)' }}>WAITING FOR REVIEW</h2>
          <span className="font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{total}</span>
        </span>
        {found?.length > 1 && (
          <button
            type="button"
            disabled={!!busy}
            onClick={() => run('all', () => onReview(found, 'REGISTERED'))}
            className={quietBtn}
            style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}
          >
            {busy === 'all' ? 'Accepting…' : `Accept all ${found.length} found by ${byId[finder]?.host || 'this agent'}`}
          </button>
        )}
      </div>

      {total === 0 && (
        <p className="m-0 px-5 py-4 text-[13px]" style={{ color: 'var(--ink-muted)' }}>
          Nothing waiting. Devices found by <span className="font-mono">agent.py --discover</span>, and addresses your agents talk to that aren’t on the map, show up here.
        </p>
      )}

      {discovered.map((a, i) => (
        <div key={a.id} className="flex flex-wrap items-center gap-3.5 px-5 py-3" style={{ borderTop: i ? '1px solid var(--surf-line)' : undefined }}>
          <span aria-hidden="true" className="box-border shrink-0 mx-1" style={glyph(a)} />
          <button type="button" onClick={() => onSelect(a.id)} className="flex-[1_1_240px] min-w-0 flex flex-col gap-[3px] text-left" title="Review the details first">
            <span className="font-mono text-[13px] truncate">{a.unnamed ? a.ip : a.host} {!a.unnamed && <span style={{ color: 'var(--ink-faint)' }}>{a.ip}</span>}</span>
            <span className="text-xs truncate" style={{ color: 'var(--ink-faint)' }}>
              {[typeLabel(a.deviceType), a.vendor, a.discoveredBy && `found by ${byId[a.discoveredBy]?.host || 'an agent'}`, a.ports.length && `ports ${a.ports.map(p => p.port).join(', ')}`].filter(Boolean).join(' · ')}
            </span>
          </button>
          <span className="flex gap-2">
            <button type="button" disabled={!!busy} onClick={() => run(a.id, () => onReview([a], 'REGISTERED'))} className={acceptBtn} style={{ background: 'var(--accent)', color: '#0A0A0A' }}>
              {busy === a.id ? 'Saving…' : 'Accept'}
            </button>
            <button type="button" disabled={!!busy} onClick={() => run(a.id, () => onReview([a], 'IGNORED'))} className={quietBtn} style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
              Dismiss
            </button>
          </span>
        </div>
      ))}

      {unknown.map((u, i) => {
        const who = u.sources.map(id => byId[id]?.host).filter(Boolean);
        return (
          <div key={u.ip} className="flex flex-wrap items-center gap-3.5 px-5 py-3" style={{ borderTop: discovered.length || i ? '1px solid var(--surf-line)' : undefined }}>
            <span aria-hidden="true" className="box-border shrink-0 mx-1" style={{ ...glyph(null), borderColor: 'var(--ink-faint)' }} />
            <span className="flex-[1_1_240px] min-w-0 flex flex-col gap-[3px]">
              <span className="font-mono text-[13px] truncate">{u.ip} <span style={{ color: 'var(--ink-faint)' }}>not on the map</span></span>
              <span className="text-xs truncate" style={{ color: 'var(--ink-faint)' }}>
                No agent found it · {who.length ? who.join(', ') : 'an agent'} connected to it {u.observations} time{u.observations === 1 ? '' : 's'}
              </span>
            </span>
            <button type="button" onClick={() => onAddAddress(u.ip)} className={quietBtn} style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}>
              Add as asset
            </button>
          </div>
        );
      })}
    </section>
  );
};

export default ReviewTray;
